import { Box, Typography } from '@mui/material'
import SettingsBox from 'src/Elements/Data/SettingsBox'
import { TextField, NumberField } from 'src/Libs/inputs/formik'
import { PhaseToggleField } from 'src/Libs/wallboxes/components/form'
import { SingleToggleSettingBox } from 'src/Elements/SystemSettings/forms'
import { UserLevel } from 'src/State/User/UserModel'
import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useUserRoles } from 'src/Hooks/user'
import { useWallboxSettingsFormHelpers } from 'src/Libs/wallboxes/hooks'
import { WallboxConfigurationModel } from 'src/Libs/wallboxes'
import { WallboxType } from 'src/Libs/wallboxes/api'
import type { WallboxPhaseType } from 'src/Libs/wallboxes/types'
import { LED_INTENSITY_MAX, LED_INTENSITY_MIN } from 'src/Libs/wallboxes/hooks/use-wallbox-settings-form-field-validations'
import { UnitTypes } from 'src/Libs/types'
import { CommonFieldTitles, CommonFieldNames, CommonFieldLabels } from './consts'

interface Props {
  model: WallboxConfigurationModel
  disabled?: boolean
  hasCableLock?: boolean
  hasLedIntensity?: boolean
}

const PHASES_CONFIG = {
  phases: new Map<WallboxType, WallboxPhaseType[]>([
    [WallboxType.DefaultE3dc, [1, 3]],
    [WallboxType.EasyConnect, [1, 2, 3]],
    [WallboxType.Bidi, [3]],
    [WallboxType.MultiConnect, [1, 3]],
    [WallboxType.Efy, [1, 3]],
  ]),
  wallboxTypesWithAutoMode: [WallboxType.MultiConnect, WallboxType.Efy],
  autoSwitchValue: 'auto',
}

const CommonFieldSet: React.FC<Props> = ({ model, disabled = false, hasCableLock = false, hasLedIntensity = false }) => {
  const { t } = useCustomTranslation()
  const { currentRole } = useUserRoles()
  const { maxChargeCurrentLimitSetBuilder, isMobile } = useWallboxSettingsFormHelpers(model)

  return (
    <Box>
      <Typography variant="h2" sx={{ mb: 2 }}>
        {t(CommonFieldTitles.title)}
      </Typography>
      <SettingsBox
        disabled={disabled}
        title={t(CommonFieldTitles.deviceName)}
        suffix={<TextField fullWidth={isMobile} name={CommonFieldNames.deviceName} label={t(CommonFieldLabels.deviceName)} disabled={disabled} />}
      />
      <SettingsBox
        disabled={disabled}
        title={t(CommonFieldTitles.numberPhases)}
        suffix={
          <PhaseToggleField
            numberPhasesFieldName={CommonFieldNames.numberPhases}
            autoPhaseSwitchEnabledFieldName={CommonFieldNames.autoPhaseSwitchEnabled}
            model={model}
            disabled={disabled}
            phaseConfig={PHASES_CONFIG}
          />
        }
      />
      <SingleToggleSettingBox title={t(CommonFieldTitles.sunModeActive)} name={CommonFieldNames.sunModeActive} disabled={disabled} />
      <SettingsBox
        disabled={disabled}
        title={t(CommonFieldTitles.minChargeCurrent)}
        suffix={
          <NumberField
            fullWidth={isMobile}
            name={CommonFieldNames.minChargeCurrent}
            label={t(CommonFieldLabels.minChargeCurrent)}
            suffix={UnitTypes.ampere}
            disabled={disabled}
          />
        }
      />
      {maxChargeCurrentLimitSetBuilder(currentRole as UserLevel, disabled)}
      {hasCableLock && <SingleToggleSettingBox title={t(CommonFieldTitles.cableLock)} name={CommonFieldNames.cableLock} disabled={disabled} />}
      {hasLedIntensity && (
        <SettingsBox
          disabled={disabled}
          title={t(CommonFieldTitles.ledIntensity)}
          suffix={
            <NumberField
              fullWidth={isMobile}
              name={CommonFieldNames.ledIntensity}
              label={t(CommonFieldLabels.ledIntensity)}
              suffix={UnitTypes.percentage}
              min={LED_INTENSITY_MIN}
              max={LED_INTENSITY_MAX}
              disabled={disabled}
            />
          }
        />
      )}
    </Box>
  )
}

export default CommonFieldSet
